import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sparkles, Search, Loader2, Mail, Phone, Linkedin, Globe, Building2,
  Lock, CheckCircle2, PlusCircle, Wallet, MapPin, User, Users, TrendingUp,
  Briefcase, Facebook, Instagram, Youtube, Twitter, Calendar, ChevronDown,
  ChevronUp, FileText, LayoutGrid, Table as TableIcon, Download,
} from "lucide-react";

const DEFAULT_PRICES = {
  person_email:  { credits: 5,  label: "Προσωπικό email υπευθύνου" },
  person_phone:  { credits: 30, label: "Προσωπικό τηλέφωνο υπευθύνου" },
  company_phone: { credits: 1,  label: "Τηλέφωνο εταιρείας" },
};

const TARGET_TYPES = [
  { v: "wholesalers",   l: "Χονδρέμποροι" },
  { v: "distributors",  l: "Διανομείς" },
  { v: "importers",     l: "Εισαγωγείς" },
  { v: "retailers",     l: "Λιανοπωλητές" },
  { v: "manufacturers", l: "Παραγωγοί" },
  { v: "resellers",     l: "Μεταπωλητές" },
  { v: "ho.re.ca",      l: "HoReCa" },
  { v: "supermarkets",  l: "Supermarkets" },
];

const COUNTRIES = [
  "Germany", "Italy", "France", "Spain", "Netherlands", "Belgium", "Austria", "Switzerland",
  "United Kingdom", "Ireland", "Sweden", "Denmark", "Norway", "Finland", "Poland", "Czech Republic",
  "Romania", "Bulgaria", "Serbia", "Croatia", "Slovenia", "Hungary", "Portugal", "Cyprus",
  "United States", "Canada", "Australia", "United Arab Emirates", "Saudi Arabia", "Israel", "Turkey",
];

const SOCIAL_META = {
  facebook:  { icon: Facebook, color: "text-blue-600" },
  instagram: { icon: Instagram, color: "text-pink-600" },
  x:         { icon: Twitter, color: "text-slate-800" },
  youtube:   { icon: Youtube, color: "text-red-600" },
  tiktok:    { icon: Users, color: "text-slate-800" },
  pinterest: { icon: Users, color: "text-red-500" },
};

const creditsToEur = (c) => (c * 0.01).toFixed(2);

export default function AILeadFinder() {
  const [query, setQuery] = useState("");
  const [industry, setIndustry] = useState("");
  const [product, setProduct] = useState("");
  const [targetType, setTargetType] = useState("");
  const [country, setCountry] = useState("");
  const [maxResults, setMaxResults] = useState(10);
  const [requireLinkedin, setRequireLinkedin] = useState(false);

  const [balance, setBalance] = useState(null);
  const [prices, setPrices] = useState(DEFAULT_PRICES);
  const [search, setSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unlocks, setUnlocks] = useState({});
  const [unlocking, setUnlocking] = useState({});
  const [reps, setReps] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [view, setView] = useState("table"); // "table" | "cards"

  const loadBalance = async () => {
    try {
      const { data } = await api.get("/ai-leads/balance");
      setBalance(data);
    } catch {
      setBalance({ credits: 0, usd_value: 0 });
    }
  };

  useEffect(() => {
    loadBalance();
    api.get("/ai-leads/unlock-prices").then((r) => setPrices(r.data)).catch(() => {});
    api.get("/users?role=sales_rep").then((r) => setReps(r.data || [])).catch(() => {});
  }, []);

  async function doSearch() {
    if (!query.trim() && !industry && !product && !targetType && !country) {
      toast.error("Συμπληρώστε τουλάχιστον ένα φίλτρο ή γράψτε περιγραφή");
      return;
    }
    setLoading(true);
    setUnlocks({});
    setSelected(new Set());
    try {
      const { data } = await api.post("/ai-leads/search", {
        query: (query.trim() || industry || product || "B2B companies"),
        industry: industry.trim() || undefined,
        product: product.trim() || undefined,
        target_type: targetType || undefined,
        country: country || undefined,
        max_results: Number(maxResults) || 10,
        require_linkedin: requireLinkedin,
      });
      setSearch(data);
      const costMsg = data.credits_charged > 0
        ? ` — κόστος €${(data.credits_charged * 0.01).toFixed(2)}`
        : " — ΔΩΡΕΑΝ";
      toast.success(`Βρέθηκαν ${data.count} υπεύθυνοι${costMsg}`);
      loadBalance();
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Δεν έχετε αρκετά credits");
      else toast.error(e?.response?.data?.detail || "Σφάλμα αναζήτησης");
    } finally {
      setLoading(false);
    }
  }

  async function unlock(leadId, field) {
    const key = `${leadId}:${field}`;
    if (unlocking[key]) return;
    const price = prices[field]?.credits ?? 0;
    const label = prices[field]?.label || field;
    if (price > 0 && !window.confirm(`Ξεκλείδωμα "${label}" για ~€${creditsToEur(price)};`)) return;
    setUnlocking((s) => ({ ...s, [key]: true }));
    try {
      const { data } = await api.post("/ai-leads/unlock", {
        search_id: search.search_id, lead_id: leadId, field,
      });
      setUnlocks((prev) => ({
        ...prev,
        [leadId]: { ...(prev[leadId] || {}), [field]: data.value },
      }));
      if (!data.value) toast.info("Δεν βρέθηκε (δεν χρεωθήκατε)");
      else {
        toast.success(`Ξεκλειδώθηκε: ${label}`);
        if (data.credits_charged > 0) loadBalance();
      }
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Δεν έχετε αρκετά credits");
      else toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally {
      setUnlocking((s) => ({ ...s, [key]: false }));
    }
  }

  function toggleSelect(leadId) {
    const n = new Set(selected);
    if (n.has(leadId)) n.delete(leadId); else n.add(leadId);
    setSelected(n);
  }

  async function importSelected() {
    if (selected.size === 0) { toast.error("Επιλέξτε τουλάχιστον ένα lead"); return; }
    try {
      const { data } = await api.post("/ai-leads/import", {
        search_id: search.search_id,
        lead_ids: Array.from(selected),
        assign_to_user_id: assignTo || undefined,
      });
      toast.success(`Εισαγωγή ${data.total_created} leads στο CRM`);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    }
  }

  function downloadCsv() {
    if (!leads.length) return;
    const headers = [
      "Όνομα","Επώνυμο","Θέση","LinkedIn υπευθύνου",
      "Εταιρεία","Website","Domain","LinkedIn εταιρείας",
      "Email εταιρείας (1)","Email εταιρείας (2)","Email εταιρείας (3)",
      "Χώρα","Πόλη","Κλάδος","NACE περιγραφή","Μέγεθος","Ιδρύθηκε",
      "Hiring","Website traffic","Growth %","Ετήσια έσοδα (€)",
      "Facebook","Instagram","X","YouTube","TikTok",
      "Νομική επωνυμία","Νομική μορφή","VAT","Tax ID",
      "Περιγραφή",
      "Προσωπικό email (unlocked)","Προσωπικό τηλέφωνο (unlocked)","Τηλέφωνο εταιρείας (unlocked)",
    ];
    const rows = leads.map((l) => {
      const u = unlocks[l.lead_id] || {};
      const emails = l.company_emails || [];
      const soc = l.company_socials || {};
      const legal = l.company_legal || {};
      return [
        l.first_name, l.last_name, l.job_title, l.person_linkedin,
        l.company_name, l.company_website, l.company_domain, l.company_linkedin,
        emails[0] || "", emails[1] || "", emails[2] || "",
        l.company_country, l.company_city, l.company_industry, l.company_industry_description,
        l.company_size, l.company_founded,
        l.company_hiring ? "Yes" : "No",
        l.company_traffic, l.company_traffic_growth, l.company_revenue_annual,
        soc.facebook || "", soc.instagram || "", soc.x || "", soc.youtube || "", soc.tiktok || "",
        legal.name || "", legal.form || "", legal.vat || "", legal.tax_id || "",
        l.company_description,
        u.person_email || "", u.person_phone || "", u.company_phone || "",
      ];
    });

    // Build a proper .xlsx workbook (SheetJS) — real Excel columns, Greek-safe, no CSV locale issues
    const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    // Reasonable column widths
    sheet["!cols"] = headers.map((h, i) => {
      const maxLen = Math.max(h.length, ...rows.map((r) => String(r[i] ?? "").length));
      return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Leads");
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    XLSX.writeFile(wb, `ai-leads-${ts}.xlsx`);
    toast.success(`Λήψη ${leads.length} leads σε Excel`);
  }

  const leads = search?.leads || [];

  return (
    <div className="space-y-6" data-testid="ai-lead-finder-page">
      {/* Header + credits */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            AI Lead Finder
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Αναζητήστε υπεύθυνους επικοινωνίας με δωρεάν profile και ξεκλειδώστε paid στοιχεία ανά πεδίο.
          </p>
        </div>
        <Card className="border-indigo-100 bg-indigo-50/50 shrink-0" data-testid="ai-credits-card">
          <CardContent className="p-3 flex items-center gap-3">
            <Wallet className="w-5 h-5 text-indigo-600" />
            <div>
              <div className="text-xs text-slate-600">Διαθέσιμα credits</div>
              <div className="text-lg font-bold text-indigo-900">
                {balance ? balance.credits : "…"}
                <span className="text-xs font-normal text-slate-500 ml-1">
                  (~€{balance ? creditsToEur(balance.credits) : "0.00"})
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search form */}
      <Card data-testid="ai-search-card">
        <CardHeader>
          <CardTitle className="text-base">Νέα αναζήτηση</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder='π.χ. "εισαγωγείς βιολογικών τροφίμων με 20+ υπαλλήλους"'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="ai-query"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <LabeledInput label="Κλάδος" value={industry} onChange={setIndustry} placeholder="Τρόφιμα" testId="ai-industry" />
            <LabeledInput label="Προϊόν" value={product} onChange={setProduct} placeholder="ελαιόλαδο" testId="ai-product" />
            <LabeledSelect label="Τύπος στόχου" value={targetType} onChange={setTargetType} options={TARGET_TYPES} testId="ai-target-type" />
            <LabeledSelect label="Χώρα" value={country} onChange={setCountry} options={COUNTRIES.map(c => ({ v: c, l: c }))} testId="ai-country" />
          </div>
          <div className="flex flex-wrap items-end gap-3 pt-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1.5 block uppercase tracking-wider">Αριθμός leads</label>
              <Input type="number" min={1} max={100} value={maxResults} onChange={(e) => setMaxResults(e.target.value)} className="w-28" data-testid="ai-max-results" />
            </div>
            <Button onClick={doSearch} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ai-search-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              {loading ? "Αναζήτηση…" : "Αναζήτηση"}
            </Button>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none" data-testid="ai-require-linkedin-label">
              <input
                type="checkbox"
                checked={requireLinkedin}
                onChange={(e) => setRequireLinkedin(e.target.checked)}
                className="w-4 h-4 accent-indigo-600"
                data-testid="ai-require-linkedin"
              />
              <Linkedin className="w-3.5 h-3.5 text-blue-600" />
              Μόνο με LinkedIn υπευθύνου
            </label>
            <CostPreview maxResults={Number(maxResults) || 10} hasCountry={!!country} />
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {leads.length > 0 && (
        <Card data-testid="ai-results-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base">
                {leads.length} υπεύθυνοι επικοινωνίας
                {search.country_filter && <Badge variant="outline" className="ml-2 text-xs">🌍 {search.country_filter}</Badge>}
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="ai-view-toggle">
                  <button
                    onClick={() => setView("table")}
                    className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${view === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid="ai-view-table"
                  >
                    <TableIcon className="w-3.5 h-3.5" /> Πίνακας
                  </button>
                  <button
                    onClick={() => setView("cards")}
                    className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${view === "cards" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid="ai-view-cards"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" /> Κάρτες
                  </button>
                </div>
                <Button variant="outline" onClick={downloadCsv} data-testid="ai-download-csv-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Excel (.xlsx)
                </Button>
                {reps.length > 0 && (
                  <select className="h-9 border border-slate-300 rounded-md px-2 text-sm bg-white"
                    value={assignTo} onChange={(e) => setAssignTo(e.target.value)} data-testid="ai-assign-to">
                    <option value="">— Ανάθεση σε Rep —</option>
                    {reps.map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
                  </select>
                )}
                <Button onClick={importSelected} disabled={selected.size === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ai-import-btn">
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Εισαγωγή στο CRM ({selected.size})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {view === "table" ? (
              <LeadTable
                leads={leads}
                unlocks={unlocks}
                prices={prices}
                selected={selected}
                onToggleSelect={toggleSelect}
                onUnlock={unlock}
                unlocking={unlocking}
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {leads.map((lead) => (
                  <LeadCard
                    key={lead.lead_id}
                    lead={lead}
                    unlocks={unlocks[lead.lead_id] || {}}
                    prices={prices}
                    selected={selected.has(lead.lead_id)}
                    onToggleSelect={() => toggleSelect(lead.lead_id)}
                    onUnlock={(f) => unlock(lead.lead_id, f)}
                    unlocking={unlocking}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 mb-1.5 block uppercase tracking-wider">{label}</label>
      <Input placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

function CostPreview({ maxResults, hasCountry }) {
  const page = Math.min(maxResults + (hasCountry ? Math.min(maxResults, 20) : 0), 100);
  const credits = Math.max(0, page - 10);
  const eur = (credits * 0.01).toFixed(2);
  return (
    <span className="text-xs text-slate-500" data-testid="ai-cost-preview">
      💡 {credits === 0
        ? "ΔΩΡΕΑΝ (πρώτοι 10)"
        : <>Εκτιμώμενο κόστος: <span className="font-semibold text-indigo-700">€{eur}</span> ({credits} credits · page_size {page})</>}
    </span>
  );
}
function LabeledSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 mb-1.5 block uppercase tracking-wider">{label}</label>
      <select className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm bg-white" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        <option value="">— Επιλέξτε —</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function LeadCard({ lead, unlocks, prices, selected, onToggleSelect, onUnlock, unlocking }) {
  const [expanded, setExpanded] = useState(false);
  const key = (f) => `${lead.lead_id}:${f}`;
  const emails = lead.company_emails || [];
  const socials = lead.company_socials || {};
  const hasExtras = lead.company_description || Object.keys(socials).length > 0 || lead.company_legal || lead.company_traffic;

  return (
    <div className={`p-5 ${selected ? "bg-indigo-50/60" : "hover:bg-slate-50/60"} transition-colors`} data-testid={`ai-lead-${lead.lead_id}`}>
      <div className="flex items-start gap-4">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} className="mt-1.5" data-testid={`ai-lead-select-${lead.lead_id}`} />
        <div className="flex-1 min-w-0 space-y-3">
          {/* Row 1: Person + Company */}
          <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <User className="w-4 h-4 text-slate-400" />
                <span className="font-semibold text-slate-900">{lead.full_name || "—"}</span>
                {lead.person_linkedin && (
                  <a href={lead.person_linkedin} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800" title="LinkedIn υπευθύνου (δωρεάν)">
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
              </div>
              {lead.job_title && <div className="text-xs text-slate-500 ml-6">· {lead.job_title}</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Building2 className="w-4 h-4 text-slate-400" />
                <span className="font-medium text-slate-900">{lead.company_name || "—"}</span>
                {lead.company_website && (
                  <a href={lead.company_website} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 text-xs underline">
                    {(lead.company_domain || "website").replace(/^www\./, "")}
                  </a>
                )}
                {lead.company_linkedin && (
                  <a href={lead.company_linkedin} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800" title="LinkedIn εταιρείας (δωρεάν)">
                    <Linkedin className="w-4 h-4" />
                  </a>
                )}
              </div>
              <div className="mt-1 flex items-center gap-x-3 gap-y-1 text-xs text-slate-500 flex-wrap">
                {lead.company_country && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {lead.company_city ? `${lead.company_city}, ${lead.company_country}` : lead.company_country}
                  </span>
                )}
                {lead.company_industry && <span>· {lead.company_industry}</span>}
                {lead.company_size && <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {lead.company_size}</span>}
                {lead.company_founded && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {lead.company_founded}</span>}
                {lead.company_hiring && <Badge variant="outline" className="text-[10px] py-0 h-4 bg-emerald-50 text-emerald-700 border-emerald-200">Hiring</Badge>}
              </div>
            </div>
          </div>

          {/* Row 2: Free company emails */}
          {emails.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-slate-500">📧 Emails εταιρείας:</span>
              {emails.slice(0, 3).map((e, i) => (
                <a key={i} href={`mailto:${e}`} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-emerald-800 hover:bg-emerald-100">
                  <Mail className="w-3 h-3" />{e}
                </a>
              ))}
            </div>
          )}

          {/* Row 3: Paid unlocks (only 3) */}
          <div className="flex items-center gap-2 flex-wrap">
            <UnlockChip
              icon={Mail}
              label="Προσωπικό email"
              price={prices.person_email?.credits}
              value={unlocks.person_email}
              unlocked={"person_email" in unlocks}
              loading={unlocking[key("person_email")]}
              onClick={() => onUnlock("person_email")}
              testId={`unlock-person-email-${lead.lead_id}`}
            />
            <UnlockChip
              icon={Phone}
              label="Προσωπικό τηλέφωνο"
              price={prices.person_phone?.credits}
              value={unlocks.person_phone}
              unlocked={"person_phone" in unlocks}
              loading={unlocking[key("person_phone")]}
              onClick={() => onUnlock("person_phone")}
              testId={`unlock-person-phone-${lead.lead_id}`}
            />
            <UnlockChip
              icon={Phone}
              label="Τηλ. εταιρείας"
              price={prices.company_phone?.credits}
              value={unlocks.company_phone}
              unlocked={"company_phone" in unlocks}
              loading={unlocking[key("company_phone")]}
              onClick={() => onUnlock("company_phone")}
              testId={`unlock-company-phone-${lead.lead_id}`}
            />

            {/* Social media (FREE) */}
            {Object.entries(socials).map(([k, url]) => {
              const meta = SOCIAL_META[k];
              if (!meta || !url) return null;
              const Icon = meta.icon;
              return (
                <a key={k} href={url} target="_blank" rel="noreferrer" className={`inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-200 hover:border-slate-300 hover:bg-slate-50 ${meta.color}`} title={`${k} (δωρεάν)`}>
                  <Icon className="w-3.5 h-3.5" />
                </a>
              );
            })}

            {hasExtras && (
              <button onClick={() => setExpanded(!expanded)} className="ml-auto text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`toggle-extras-${lead.lead_id}`}>
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {expanded ? "Λιγότερα" : "Περισσότερα δωρεάν στοιχεία"}
              </button>
            )}
          </div>

          {/* Expanded: description + extras */}
          {expanded && (
            <div className="mt-3 border-t border-slate-100 pt-3 space-y-2 text-sm text-slate-700" data-testid={`extras-${lead.lead_id}`}>
              {lead.company_description && (
                <div className="flex gap-2">
                  <FileText className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  <p className="text-slate-600 italic">"{lead.company_description}"</p>
                </div>
              )}
              {lead.company_industry_description && (
                <div className="flex gap-2 text-xs">
                  <Briefcase className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                  <span className="text-slate-600">{lead.company_industry_description}</span>
                </div>
              )}
              {(lead.company_traffic || lead.company_revenue_annual) && (
                <div className="flex items-center gap-4 text-xs text-slate-600">
                  {lead.company_traffic != null && (
                    <span className="inline-flex items-center gap-1">
                      <TrendingUp className="w-3.5 h-3.5" /> {lead.company_traffic.toLocaleString()} μηνιαίες επισκέψεις
                      {lead.company_traffic_growth != null && (
                        <span className={lead.company_traffic_growth >= 0 ? "text-emerald-600" : "text-red-500"}>
                          {" "}({lead.company_traffic_growth >= 0 ? "+" : ""}{lead.company_traffic_growth}%)
                        </span>
                      )}
                    </span>
                  )}
                  {lead.company_revenue_annual && <span>· €{lead.company_revenue_annual.toLocaleString()} ετ. έσοδα</span>}
                </div>
              )}
              {lead.company_legal && Object.keys(lead.company_legal).length > 0 && (
                <div className="text-xs text-slate-600">
                  <span className="font-semibold">Νομικά:</span>{" "}
                  {lead.company_legal.name && <span>{lead.company_legal.name}</span>}
                  {lead.company_legal.form && <span> · {lead.company_legal.form}</span>}
                  {lead.company_legal.vat && <span> · VAT: {lead.company_legal.vat}</span>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LeadTable({ leads, unlocks, prices, selected, onToggleSelect, onUnlock, unlocking }) {
  return (
    <div className="overflow-x-auto" data-testid="ai-lead-table">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-y border-slate-200">
          <tr className="text-left text-slate-600 uppercase tracking-wider text-[10px] font-bold">
            <Th sticky>☑</Th>
            <Th sticky left="36">Υπεύθυνος</Th>
            <Th>Θέση</Th>
            <Th>Εταιρεία</Th>
            <Th>Χώρα</Th>
            <Th>Πόλη</Th>
            <Th>Κλάδος</Th>
            <Th>NACE</Th>
            <Th>Μέγεθος</Th>
            <Th>Ιδρύθηκε</Th>
            <Th>Hiring</Th>
            <Th>Traffic</Th>
            <Th>Έσοδα</Th>
            <Th>Website</Th>
            <Th>LinkedIn</Th>
            <Th>Email εταιρείας</Th>
            <Th>Socials</Th>
            <Th>Νομικά</Th>
            <Th>Περιγραφή</Th>
            <Th>Προσωπικό email</Th>
            <Th>Προσωπικό τηλ.</Th>
            <Th>Τηλ. εταιρείας</Th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => {
            const u = unlocks[l.lead_id] || {};
            const emails = l.company_emails || [];
            const soc = l.company_socials || {};
            const legal = l.company_legal || {};
            const isSel = selected.has(l.lead_id);
            const key = (f) => `${l.lead_id}:${f}`;
            return (
              <tr key={l.lead_id} className={`border-b border-slate-100 ${isSel ? "bg-indigo-50/60" : "hover:bg-slate-50"}`} data-testid={`ai-table-row-${l.lead_id}`}>
                <Td sticky><input type="checkbox" checked={isSel} onChange={() => onToggleSelect(l.lead_id)} data-testid={`ai-lead-select-${l.lead_id}`} /></Td>
                <Td sticky left="36" className="font-semibold text-slate-900 whitespace-nowrap">
                  {l.full_name}
                  {l.person_linkedin && (
                    <a href={l.person_linkedin} target="_blank" rel="noreferrer" className="ml-1 inline-flex align-middle text-blue-600 hover:text-blue-800">
                      <Linkedin className="w-3 h-3 inline" />
                    </a>
                  )}
                </Td>
                <Td className="whitespace-nowrap">{l.job_title || "—"}</Td>
                <Td className="font-medium text-slate-900 whitespace-nowrap">{l.company_name || "—"}</Td>
                <Td className="whitespace-nowrap">{l.company_country || "—"}</Td>
                <Td className="whitespace-nowrap">{l.company_city || "—"}</Td>
                <Td className="whitespace-nowrap">{l.company_industry || "—"}</Td>
                <Td className="whitespace-nowrap text-slate-500" title={l.company_industry_description || ""}>
                  {l.company_industry_description ? l.company_industry_description.slice(0, 30) + (l.company_industry_description.length > 30 ? "…" : "") : "—"}
                </Td>
                <Td className="whitespace-nowrap">{l.company_size || "—"}</Td>
                <Td className="whitespace-nowrap">{l.company_founded || "—"}</Td>
                <Td className="whitespace-nowrap">
                  {l.company_hiring ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">Ναι</Badge> : <span className="text-slate-400">—</span>}
                </Td>
                <Td className="whitespace-nowrap">
                  {l.company_traffic != null ? (
                    <span>
                      {l.company_traffic.toLocaleString()}
                      {l.company_traffic_growth != null && (
                        <span className={`ml-1 ${l.company_traffic_growth >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                          ({l.company_traffic_growth >= 0 ? "+" : ""}{l.company_traffic_growth}%)
                        </span>
                      )}
                    </span>
                  ) : "—"}
                </Td>
                <Td className="whitespace-nowrap">{l.company_revenue_annual ? `€${l.company_revenue_annual.toLocaleString()}` : "—"}</Td>
                <Td>
                  {l.company_website ? (
                    <a href={l.company_website} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline whitespace-nowrap">
                      {(l.company_domain || "").replace(/^www\./, "") || "link"}
                    </a>
                  ) : "—"}
                </Td>
                <Td>
                  {l.company_linkedin ? (
                    <a href={l.company_linkedin} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
                      <Linkedin className="w-3.5 h-3.5" />
                    </a>
                  ) : "—"}
                </Td>
                <Td>
                  {emails.length > 0 ? (
                    <a href={`mailto:${emails[0]}`} className="text-emerald-700 hover:underline whitespace-nowrap">{emails[0]}</a>
                  ) : "—"}
                  {emails.length > 1 && <span className="text-slate-400 text-[10px]"> +{emails.length - 1}</span>}
                </Td>
                <Td>
                  <div className="flex items-center gap-1">
                    {Object.entries(soc).map(([k, url]) => {
                      const meta = SOCIAL_META[k];
                      if (!meta || !url) return null;
                      const Icon = meta.icon;
                      return (
                        <a key={k} href={url} target="_blank" rel="noreferrer" className={`inline-flex ${meta.color}`} title={k}>
                          <Icon className="w-3.5 h-3.5" />
                        </a>
                      );
                    })}
                    {Object.keys(soc).length === 0 && <span>—</span>}
                  </div>
                </Td>
                <Td className="whitespace-nowrap text-slate-500">
                  {legal.name || legal.form || legal.vat ? (
                    <span title={`${legal.name || ""} ${legal.form || ""} ${legal.vat ? `VAT ${legal.vat}` : ""}`}>
                      {(legal.name || legal.form || legal.vat || "").slice(0, 20)}…
                    </span>
                  ) : "—"}
                </Td>
                <Td className="max-w-xs">
                  {l.company_description ? (
                    <span className="text-slate-600 italic line-clamp-2 block" title={l.company_description}>
                      {l.company_description.slice(0, 80)}…
                    </span>
                  ) : "—"}
                </Td>
                <Td><InlineUnlock f="person_email" l={l} u={u} prices={prices} onUnlock={onUnlock} loading={unlocking[key("person_email")]} /></Td>
                <Td><InlineUnlock f="person_phone" l={l} u={u} prices={prices} onUnlock={onUnlock} loading={unlocking[key("person_phone")]} /></Td>
                <Td><InlineUnlock f="company_phone" l={l} u={u} prices={prices} onUnlock={onUnlock} loading={unlocking[key("company_phone")]} /></Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, sticky, left }) {
  const style = sticky ? { position: "sticky", left: left ? `${left}px` : 0, zIndex: 3, backgroundColor: "#f8fafc", boxShadow: left ? "2px 0 0 rgba(0,0,0,0.03)" : undefined } : undefined;
  return <th className="px-2 py-2.5 border-r border-slate-100" style={style}>{children}</th>;
}
function Td({ children, sticky, left, className = "" }) {
  const style = sticky ? { position: "sticky", left: left ? `${left}px` : 0, zIndex: 2, backgroundColor: "white", boxShadow: left ? "2px 0 0 rgba(0,0,0,0.03)" : undefined } : undefined;
  return <td className={`px-2 py-2 border-r border-slate-100 align-top ${className}`} style={style}>{children}</td>;
}

function InlineUnlock({ f, l, u, prices, onUnlock, loading }) {
  const unlocked = f in u;
  const value = u[f];
  const price = prices[f]?.credits ?? 0;
  if (unlocked && value) {
    return <span className="text-indigo-800 font-medium whitespace-nowrap">{value}</span>;
  }
  if (unlocked && !value) {
    return <span className="text-slate-400 text-[10px]">Δεν βρέθηκε</span>;
  }
  return (
    <button onClick={() => onUnlock(l.lead_id, f)} disabled={loading}
      data-testid={`unlock-${f.replace("_","-")}-${l.lead_id}`}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 whitespace-nowrap ${loading ? "opacity-60" : ""}`}>
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
      <span className="text-[10px] font-bold text-slate-600">€{(price * 0.01).toFixed(2)}</span>
    </button>
  );
}

function UnlockChip({ icon: Icon, label, price, value, unlocked, loading, onClick, testId }) {
  if (unlocked && value) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-900 text-xs font-medium" data-testid={testId}>
        <CheckCircle2 className="w-3.5 h-3.5" />
        <Icon className="w-3.5 h-3.5" />
        <span className="font-semibold">{value}</span>
      </span>
    );
  }
  if (unlocked && !value) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-xs font-medium text-slate-400" data-testid={testId}>
        <Icon className="w-3.5 h-3.5" />
        <span>Δεν βρέθηκε</span>
      </span>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={loading} data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 text-slate-700 text-xs font-medium transition-colors ${loading ? "opacity-60" : ""}`}>
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
      <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-bold tabular-nums">€{(price * 0.01).toFixed(2)}</span>
    </button>
  );
}
